/**
 * Framework content watch — the strings of the review screen and of the
 * "mapping sources" note in the Control Library.
 *
 * SAME PATTERN AS NEWS_STRINGS / EVIDENCE_STRINGS
 *   CONTENT_WATCH_STRINGS     English; the inline `defaultValue` of every lookup,
 *                             so the screen renders even if nothing registered it.
 *   CONTENT_WATCH_STRINGS_EL  Greek.
 * `registerContentWatchTranslations()` adds both to the running i18n instance
 * without touching i18n/index.ts or the locale JSON (so the parity guard, which
 * reads the JSON, is unaffected). Once the keys are merged into en.json and
 * el.json the registration is redundant but harmless: it never overwrites.
 *
 * WHAT IS NOT TRANSLATED
 * The `summary` of a review item, the diff, and the reasons people type are
 * data, stored as written (English for summaries). The screen says so.
 */

import i18n from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const CONTENT_WATCH_STRINGS = {
  'nav.contentWatch': 'Content watch',
  'nav.shortContentWatch': 'Watch',

  'contentWatch.title': 'Framework content watch',
  'contentWatch.subtitle':
    'Upstream sources are checked automatically. Nothing they say is ever applied automatically: every change waits here for a person to decide.',
  'contentWatch.rule':
    'Detect automatically, apply deliberately. Accepting an item records that you reviewed it and agree. It does not change any mapping; changes to the catalogue are made by hand.',
  'contentWatch.loadFailed': 'Could not load the review queue.',
  'contentWatch.retry': 'Try again',
  'contentWatch.englishNote': 'Summaries and diffs are generated in English.',

  // Sources
  'contentWatch.sources': 'Sources we watch',
  'contentWatch.source.licence': 'Licence {{licence}}',
  'contentWatch.source.lastChecked': 'Last checked {{date}}',
  'contentWatch.source.neverChecked': 'Not checked yet',
  'contentWatch.source.lastChanged': 'Last changed {{date}}',
  'contentWatch.source.status.OK': 'Reachable',
  'contentWatch.source.status.FETCH_FAILED': 'Could not be fetched',
  'contentWatch.source.status.LICENCE_MISMATCH': 'Licence changed',
  'contentWatch.source.status.PARSE_FAILED': 'No longer readable',
  'contentWatch.source.inactive': 'Switched off',

  // Tabs
  'contentWatch.tab.OPEN': 'Waiting for a decision',
  'contentWatch.tab.DECIDED': 'Decided',
  'contentWatch.tab.ALL': 'All',
  'contentWatch.empty.open.title': 'Nothing is waiting for a decision',
  'contentWatch.empty.open.body': 'When an upstream source changes, an item will appear here.',
  'contentWatch.empty.decided.title': 'No decisions recorded yet',
  'contentWatch.empty.decided.body': 'Accepted, rejected and recorded differences are kept here for good.',

  // Item
  'contentWatch.kind.BASELINE': 'First sight',
  'contentWatch.kind.CHANGE': 'Changed',
  'contentWatch.kind.LICENCE': 'Licence problem',
  'contentWatch.kind.UNPARSEABLE': 'Unreadable',
  'contentWatch.status.OPEN': 'Open',
  'contentWatch.status.ACCEPTED': 'Accepted',
  'contentWatch.status.REJECTED': 'Rejected',
  'contentWatch.status.DIFFERS': 'We differ',
  'contentWatch.detected': 'Detected {{date}}',
  'contentWatch.showDiff': 'Show what changed',
  'contentWatch.hideDiff': 'Hide details',

  // Detail
  'contentWatch.detail.loadFailed': 'Could not load this item.',
  'contentWatch.detail.counts':
    '{{before}} → {{after}} pairs: {{added}} added, {{removed}} removed, {{changed}} changed relationship.',
  'contentWatch.detail.countsBaseline': '{{after}} pairs recorded. Only the references we also cite are listed below.',
  'contentWatch.detail.groups': 'By requirement',
  'contentWatch.detail.groupsHint':
    'Grouped by the {{framework}} reference, which is the same numbering we cite. Upstream ids are on the other side of each pair.',
  'contentWatch.detail.cited': 'We cite this',
  'contentWatch.detail.notCited': 'We do not cite this',
  'contentWatch.detail.added': 'Added',
  'contentWatch.detail.removed': 'Removed',
  'contentWatch.detail.changed': 'Relationship changed',
  'contentWatch.detail.present': 'Related upstream',
  'contentWatch.detail.truncatedLists': 'List shortened; the counts above are complete.',
  'contentWatch.detail.truncatedGroups': 'Only the first groups are shown; the counts above are complete.',
  'contentWatch.detail.noGroups': 'No individual requirement changed.',
  'contentWatch.detail.ours': 'Our catalogue mappings that cite the same requirement',
  'contentWatch.detail.oursHint':
    'As they stood when this was detected. “Related” means a sub-clause or parent clause of the same reference.',
  'contentWatch.detail.oursNone': 'None of our catalogue mappings cite a requirement affected by this.',
  'contentWatch.detail.related': 'Related',
  'contentWatch.detail.coverageFull': 'Full',
  'contentWatch.detail.coveragePartial': 'Partial',
  'contentWatch.detail.noUpstream': 'References we cite that this source never mentions',
  'contentWatch.detail.noUpstreamHint':
    'Not an error: the annex is narrower than our catalogue. Listed so a gap is visible rather than assumed.',
  'contentWatch.detail.problem': 'What the job found',

  // Decision
  'contentWatch.decide.heading': 'Your decision',
  'contentWatch.decide.accept': 'Accept',
  'contentWatch.decide.reject': 'Reject',
  'contentWatch.decide.differs': 'Record why we differ',
  'contentWatch.decide.accept.help': 'We have reviewed this and agree. Writes nothing to the catalogue.',
  'contentWatch.decide.reject.help':
    'We do not adopt this. The reason is kept permanently and is the note you show an auditor.',
  'contentWatch.decide.differs.help':
    'We knowingly hold a different position from upstream. Say what it is and why.',
  'contentWatch.decide.note': 'Reason',
  'contentWatch.decide.noteOptional': 'Note (optional)',
  'contentWatch.decide.noteHint': 'At least {{min}} characters. Written once, kept permanently, and cannot be edited afterwards.',
  'contentWatch.decide.confirm': 'Record decision',
  'contentWatch.decide.cancel': 'Cancel',
  'contentWatch.decide.permanent': 'This cannot be undone or edited.',
  'contentWatch.decide.saving': 'Saving…',
  'contentWatch.decided.by': 'Decided {{date}} by {{name}}',
  'contentWatch.decided.noNote': 'No note was recorded.',
  'contentWatch.decided.noChange': 'No mapping was changed by this decision.',

  // Errors (looked up by useApiError as error.<CODE>)
  'error.CONTENT_REVIEW_NOTE_REQUIRED': 'Give a reason of at least {{min}} characters.',
  'error.CONTENT_REVIEW_ALREADY_DECIDED': 'Someone has already decided this item. The first decision stands.',
  'error.CONTENT_REVIEW_NOT_FOUND': 'That item no longer exists.',

  // Control Library note
  'mappingSources.title': 'Mapping sources',
  'mappingSources.body':
    'Our mappings are our own assessment, written against published clause numbers. Where we compare them with the European Union Agency for Cybersecurity (ENISA) mappings of the NIS2 implementing regulation to ISO/IEC 27001:2022 and NIST CSF 2.0, differences are put to a person to decide, never applied automatically.',
  'mappingSources.changes':
    'Statement of changes (CC BY 4.0): we do not republish or alter ENISA’s mappings. We read them to compare against ours; any difference we record is our own note.',
  'mappingSources.status.reviewed_one': '{{count}} comparison reviewed',
  'mappingSources.status.reviewed_other': '{{count}} comparisons reviewed',
  'mappingSources.status.open_one': '{{count}} waiting for review',
  'mappingSources.status.open_other': '{{count}} waiting for review',
  'mappingSources.status.differences_one': '{{count}} difference we have recorded and explained',
  'mappingSources.status.differences_other': '{{count}} differences we have recorded and explained',
  'mappingSources.status.none': 'No comparison has been reviewed yet.',
  'mappingSources.lastChecked': 'Last compared {{date}}',
  'mappingSources.never': 'Not compared yet',
  'mappingSources.licence': 'Licence: {{licence}}',
  'mappingSources.open': 'Open the review queue',
} as const;

export const CONTENT_WATCH_STRINGS_EL: Record<keyof typeof CONTENT_WATCH_STRINGS, string> = {
  'nav.contentWatch': 'Παρακολούθηση περιεχομένου',
  'nav.shortContentWatch': 'Παρακολ.',

  'contentWatch.title': 'Παρακολούθηση περιεχομένου πλαισίων',
  'contentWatch.subtitle':
    'Οι εξωτερικές πηγές ελέγχονται αυτόματα. Τίποτα από όσα λένε δεν εφαρμόζεται ποτέ αυτόματα: κάθε αλλαγή περιμένει εδώ μια απόφαση από άνθρωπο.',
  'contentWatch.rule':
    'Ανίχνευση αυτόματα, εφαρμογή συνειδητά. Η αποδοχή καταγράφει ότι εξετάσατε το στοιχείο και συμφωνείτε. Δεν αλλάζει καμία αντιστοίχιση· οι αλλαγές στον κατάλογο γίνονται χειροκίνητα.',
  'contentWatch.loadFailed': 'Δεν ήταν δυνατή η φόρτωση της ουράς ελέγχου.',
  'contentWatch.retry': 'Δοκιμάστε ξανά',
  'contentWatch.englishNote': 'Οι περιλήψεις και οι διαφορές παράγονται στα αγγλικά.',

  'contentWatch.sources': 'Πηγές που παρακολουθούμε',
  'contentWatch.source.licence': 'Άδεια {{licence}}',
  'contentWatch.source.lastChecked': 'Τελευταίος έλεγχος {{date}}',
  'contentWatch.source.neverChecked': 'Δεν έχει ελεγχθεί ακόμη',
  'contentWatch.source.lastChanged': 'Τελευταία αλλαγή {{date}}',
  'contentWatch.source.status.OK': 'Προσβάσιμη',
  'contentWatch.source.status.FETCH_FAILED': 'Δεν ήταν δυνατή η λήψη',
  'contentWatch.source.status.LICENCE_MISMATCH': 'Η άδεια άλλαξε',
  'contentWatch.source.status.PARSE_FAILED': 'Δεν διαβάζεται πλέον',
  'contentWatch.source.inactive': 'Απενεργοποιημένη',

  'contentWatch.tab.OPEN': 'Αναμένουν απόφαση',
  'contentWatch.tab.DECIDED': 'Με απόφαση',
  'contentWatch.tab.ALL': 'Όλα',
  'contentWatch.empty.open.title': 'Τίποτα δεν περιμένει απόφαση',
  'contentWatch.empty.open.body': 'Όταν αλλάξει μια εξωτερική πηγή, θα εμφανιστεί εδώ ένα στοιχείο.',
  'contentWatch.empty.decided.title': 'Δεν έχει καταγραφεί ακόμη καμία απόφαση',
  'contentWatch.empty.decided.body': 'Οι αποδοχές, οι απορρίψεις και οι καταγεγραμμένες διαφορές διατηρούνται εδώ μόνιμα.',

  'contentWatch.kind.BASELINE': 'Πρώτη καταγραφή',
  'contentWatch.kind.CHANGE': 'Άλλαξε',
  'contentWatch.kind.LICENCE': 'Πρόβλημα άδειας',
  'contentWatch.kind.UNPARSEABLE': 'Μη αναγνώσιμο',
  'contentWatch.status.OPEN': 'Ανοιχτό',
  'contentWatch.status.ACCEPTED': 'Εγκρίθηκε',
  'contentWatch.status.REJECTED': 'Απορρίφθηκε',
  'contentWatch.status.DIFFERS': 'Διαφέρουμε',
  'contentWatch.detected': 'Εντοπίστηκε {{date}}',
  'contentWatch.showDiff': 'Εμφάνιση των αλλαγών',
  'contentWatch.hideDiff': 'Απόκρυψη λεπτομερειών',

  'contentWatch.detail.loadFailed': 'Δεν ήταν δυνατή η φόρτωση αυτού του στοιχείου.',
  'contentWatch.detail.counts':
    '{{before}} → {{after}} ζεύγη: {{added}} προστέθηκαν, {{removed}} αφαιρέθηκαν, {{changed}} με αλλαγμένη σχέση.',
  'contentWatch.detail.countsBaseline': 'Καταγράφηκαν {{after}} ζεύγη. Παρακάτω εμφανίζονται μόνο οι αναφορές που παραθέτουμε κι εμείς.',
  'contentWatch.detail.groups': 'Ανά απαίτηση',
  'contentWatch.detail.groupsHint':
    'Ομαδοποίηση ανά αναφορά {{framework}}, που ακολουθεί την ίδια αρίθμηση με τις δικές μας παραπομπές. Τα αναγνωριστικά της πηγής βρίσκονται στην άλλη πλευρά κάθε ζεύγους.',
  'contentWatch.detail.cited': 'Την παραθέτουμε',
  'contentWatch.detail.notCited': 'Δεν την παραθέτουμε',
  'contentWatch.detail.added': 'Προστέθηκαν',
  'contentWatch.detail.removed': 'Αφαιρέθηκαν',
  'contentWatch.detail.changed': 'Άλλαξε η σχέση',
  'contentWatch.detail.present': 'Συσχετισμένα στην πηγή',
  'contentWatch.detail.truncatedLists': 'Η λίστα συντομεύτηκε· οι αριθμοί παραπάνω είναι πλήρεις.',
  'contentWatch.detail.truncatedGroups': 'Εμφανίζονται μόνο οι πρώτες ομάδες· οι αριθμοί παραπάνω είναι πλήρεις.',
  'contentWatch.detail.noGroups': 'Καμία επιμέρους απαίτηση δεν άλλαξε.',
  'contentWatch.detail.ours': 'Αντιστοιχίσεις του καταλόγου μας που παραθέτουν την ίδια απαίτηση',
  'contentWatch.detail.oursHint':
    'Όπως ήταν τη στιγμή του εντοπισμού. Το «Συγγενές» σημαίνει υποπαράγραφο ή γονική παράγραφο της ίδιας αναφοράς.',
  'contentWatch.detail.oursNone': 'Καμία αντιστοίχιση του καταλόγου μας δεν παραθέτει απαίτηση που επηρεάζεται.',
  'contentWatch.detail.related': 'Συγγενές',
  'contentWatch.detail.coverageFull': 'Πλήρης',
  'contentWatch.detail.coveragePartial': 'Μερική',
  'contentWatch.detail.noUpstream': 'Αναφορές που παραθέτουμε και η πηγή δεν αναφέρει ποτέ',
  'contentWatch.detail.noUpstreamHint':
    'Δεν είναι σφάλμα: το παράρτημα είναι στενότερο από τον κατάλογό μας. Αναφέρονται ώστε το κενό να φαίνεται αντί να εικάζεται.',
  'contentWatch.detail.problem': 'Τι βρήκε η εργασία',

  'contentWatch.decide.heading': 'Η απόφασή σας',
  'contentWatch.decide.accept': 'Αποδοχή',
  'contentWatch.decide.reject': 'Απόρριψη',
  'contentWatch.decide.differs': 'Καταγραφή της διαφοράς μας',
  'contentWatch.decide.accept.help': 'Το εξετάσαμε και συμφωνούμε. Δεν γράφει τίποτα στον κατάλογο.',
  'contentWatch.decide.reject.help':
    'Δεν το υιοθετούμε. Ο λόγος διατηρείται μόνιμα και είναι η σημείωση που δείχνετε σε έναν ελεγκτή.',
  'contentWatch.decide.differs.help':
    'Κρατάμε συνειδητά διαφορετική θέση από την πηγή. Πείτε ποια είναι και γιατί.',
  'contentWatch.decide.note': 'Αιτιολόγηση',
  'contentWatch.decide.noteOptional': 'Σημείωση (προαιρετική)',
  'contentWatch.decide.noteHint': 'Τουλάχιστον {{min}} χαρακτήρες. Καταγράφεται μία φορά, διατηρείται μόνιμα και δεν μπορεί να επεξεργαστεί αργότερα.',
  'contentWatch.decide.confirm': 'Καταγραφή απόφασης',
  'contentWatch.decide.cancel': 'Ακύρωση',
  'contentWatch.decide.permanent': 'Δεν μπορεί να ανακληθεί ούτε να επεξεργαστεί.',
  'contentWatch.decide.saving': 'Αποθήκευση…',
  'contentWatch.decided.by': 'Αποφασίστηκε {{date}} από {{name}}',
  'contentWatch.decided.noNote': 'Δεν καταγράφηκε σημείωση.',
  'contentWatch.decided.noChange': 'Η απόφαση αυτή δεν άλλαξε καμία αντιστοίχιση.',

  'error.CONTENT_REVIEW_NOTE_REQUIRED': 'Δώστε αιτιολόγηση τουλάχιστον {{min}} χαρακτήρων.',
  'error.CONTENT_REVIEW_ALREADY_DECIDED': 'Κάποιος άλλος έχει ήδη αποφασίσει για αυτό το στοιχείο. Ισχύει η πρώτη απόφαση.',
  'error.CONTENT_REVIEW_NOT_FOUND': 'Το στοιχείο δεν υπάρχει πλέον.',

  'mappingSources.title': 'Πηγές αντιστοιχίσεων',
  'mappingSources.body':
    'Οι αντιστοιχίσεις μας είναι η δική μας εκτίμηση, γραμμένη με βάση δημοσιευμένους αριθμούς παραγράφων. Όταν τις συγκρίνουμε με τις αντιστοιχίσεις του Οργανισμού της Ευρωπαϊκής Ένωσης για την Κυβερνοασφάλεια (ENISA) του εκτελεστικού κανονισμού NIS2 με το ISO/IEC 27001:2022 και το NIST CSF 2.0, οι διαφορές τίθενται ενώπιον ανθρώπου για απόφαση και δεν εφαρμόζονται ποτέ αυτόματα.',
  'mappingSources.changes':
    'Δήλωση αλλαγών (CC BY 4.0): δεν αναδημοσιεύουμε ούτε τροποποιούμε τις αντιστοιχίσεις του ENISA. Τις διαβάζουμε για να τις συγκρίνουμε με τις δικές μας· κάθε διαφορά που καταγράφουμε είναι δική μας σημείωση.',
  'mappingSources.status.reviewed_one': '{{count}} σύγκριση εξετάστηκε',
  'mappingSources.status.reviewed_other': '{{count}} συγκρίσεις εξετάστηκαν',
  'mappingSources.status.open_one': '{{count}} αναμένει εξέταση',
  'mappingSources.status.open_other': '{{count}} αναμένουν εξέταση',
  'mappingSources.status.differences_one': '{{count}} διαφορά που έχουμε καταγράψει και αιτιολογήσει',
  'mappingSources.status.differences_other': '{{count}} διαφορές που έχουμε καταγράψει και αιτιολογήσει',
  'mappingSources.status.none': 'Καμία σύγκριση δεν έχει εξεταστεί ακόμη.',
  'mappingSources.lastChecked': 'Τελευταία σύγκριση {{date}}',
  'mappingSources.never': 'Δεν έχει συγκριθεί ακόμη',
  'mappingSources.licence': 'Άδεια: {{licence}}',
  'mappingSources.open': 'Άνοιγμα της ουράς ελέγχου',
};

export type ContentWatchKey = keyof typeof CONTENT_WATCH_STRINGS;
export type ContentWatchPluralKey =
  | 'mappingSources.status.reviewed'
  | 'mappingSources.status.open'
  | 'mappingSources.status.differences';

let registered = false;
export function registerContentWatchTranslations(): void {
  if (registered) return;
  registered = true;
  for (const [lng, table] of [['en', CONTENT_WATCH_STRINGS], ['el', CONTENT_WATCH_STRINGS_EL]] as const) {
    i18n.addResources(lng, 'translation', table as Record<string, string>);
  }
}
registerContentWatchTranslations();

export function useContentWatchT() {
  const { t, i18n: instance } = useTranslation();
  const tx = useCallback(
    (key: ContentWatchKey, options?: Record<string, unknown>): string =>
      t(key, { defaultValue: CONTENT_WATCH_STRINGS[key], ...options }) as string,
    [t],
  );
  /** Plural-aware: i18next picks the suffix, we pick the inline English fallback. */
  const txn = useCallback(
    (key: ContentWatchPluralKey, count: number): string =>
      t(key, {
        count,
        defaultValue: CONTENT_WATCH_STRINGS[`${key}_${count === 1 ? 'one' : 'other'}` as ContentWatchKey],
      }) as string,
    [t],
  );
  const formatDate = useCallback(
    (iso: string): string =>
      new Intl.DateTimeFormat(instance.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)),
    [instance.language],
  );
  return { tx, txn, formatDate };
}
